/**
 * GELINLIK21 French shared-core guard.
 *
 * Purpose:
 * - Keep the French storefront on the exact same server-rendered theme structure
 *   used by the default English storefront.
 * - Protect that shared structure from legacy locale-specific DOM injection.
 * - Never duplicate header, footer, collection, product or template markup.
 *
 * This file is loaded only when request.locale.iso_code == 'fr'.
 */
(() => {
  'use strict';

  if (window.G21FrenchSharedCoreGuard) return;

  const ROOTS = Object.freeze({
    header: '#header-group',
    main: '#MainContent',
    footer: '.page-wrapper > footer',
  });

  const LEGACY_SIGNATURES = Object.freeze([
    'handcrafted bridal elegance',
    'custom made for your special day',
    'registered company name:',
    'view more on instagram',
    'why should you choosee gelinlik21 ? ?',
    'made to order · custom size · worldwide delivery',
  ]);

  const snapshots = new Map();
  let frozen = false;
  let restoring = false;
  let checkQueued = false;
  let observer = null;
  let disconnectTimer = null;

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const legacyScore = () => {
    const text = normalize(document.body?.textContent);
    if (!text) return 0;

    return LEGACY_SIGNATURES.reduce(
      (score, signature) => score + (text.includes(signature) ? 1 : 0),
      0
    );
  };

  const getRoot = (selector) => document.querySelector(selector);

  const captureCleanRoots = () => {
    if (legacyScore() >= 2) return false;

    let captured = 0;
    for (const [name, selector] of Object.entries(ROOTS)) {
      const element = getRoot(selector);
      if (!element) continue;

      snapshots.set(name, element.cloneNode(true));
      captured += 1;
    }

    return captured === Object.keys(ROOTS).length;
  };

  const restoreSharedCore = () => {
    if (restoring || snapshots.size !== Object.keys(ROOTS).length) return false;
    if (legacyScore() < 2) return false;

    restoring = true;

    try {
      for (const [name, selector] of Object.entries(ROOTS)) {
        const current = getRoot(selector);
        const cleanSnapshot = snapshots.get(name);
        if (!current || !cleanSnapshot) continue;

        current.replaceWith(cleanSnapshot.cloneNode(true));
      }

      document.documentElement.dataset.g21FrenchSharedCore = 'restored';
      window.dispatchEvent(
        new CustomEvent('g21:shared-core-restored', {
          detail: { locale: 'fr', reason: 'legacy-locale-injection' },
        })
      );

      return true;
    } finally {
      queueMicrotask(() => {
        restoring = false;
      });
    }
  };

  const inspect = () => {
    checkQueued = false;
    if (restoring) return;

    if (!frozen) {
      if (legacyScore() < 2) {
        captureCleanRoots();
      } else {
        restoreSharedCore();
      }
      return;
    }

    restoreSharedCore();
  };

  const queueInspection = () => {
    if (checkQueued) return;
    checkQueued = true;
    queueMicrotask(inspect);
  };

  const freeze = () => {
    if (frozen) return;

    if (legacyScore() < 2) {
      captureCleanRoots();
    } else {
      restoreSharedCore();
    }

    frozen = true;
    document.documentElement.dataset.g21FrenchSharedCore = 'active';

    queueInspection();
    window.requestAnimationFrame(queueInspection);
    window.addEventListener('load', queueInspection, { once: true });

    for (const delay of [100, 350, 750, 1500, 3000, 6000]) {
      window.setTimeout(queueInspection, delay);
    }

    window.clearTimeout(disconnectTimer);
    disconnectTimer = window.setTimeout(() => {
      observer?.disconnect();
      document.documentElement.dataset.g21FrenchSharedCoreWatch = 'complete';
    }, 8000);
  };

  const disconnect = () => {
    observer?.disconnect();
    window.clearTimeout(disconnectTimer);
  };

  observer = new MutationObserver(queueInspection);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.G21FrenchSharedCoreGuard = Object.freeze({
    freeze,
    inspect: queueInspection,
    disconnect,
  });
})();
